import { useState, useCallback, useEffect } from 'react';

const getInitialState = (name) => {
  const STORAGE_KEY = `sn-elearning-progress_${name || 'default'}`;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return {
    sessionId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36),
    completedSections: [],
    quizScores: {},
    currentSection: null,
  };
};

export function useProgress(totalSections, userName = '') {
  const [state, setState] = useState(() => getInitialState(userName));

  useEffect(() => {
    setState(getInitialState(userName));
  }, [userName]);

  useEffect(() => {
    const STORAGE_KEY = `sn-elearning-progress_${userName || 'default'}`;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state, userName]);

  const markComplete = useCallback((sectionId) => {
    setState(prev => {
      if (prev.completedSections.includes(sectionId)) return prev;
      return { ...prev, completedSections: [...prev.completedSections, sectionId] };
    });
  }, []);

  const saveQuizScore = useCallback((quizId, score) => {
    setState(prev => ({
      ...prev,
      quizScores: { ...prev.quizScores, [quizId]: score },
    }));
  }, []);

  const setCurrentSection = useCallback((sectionId) => {
    setState(prev => ({ ...prev, currentSection: sectionId }));
  }, []);

  const isCompleted = useCallback((sectionId) => {
    return state.completedSections.includes(sectionId);
  }, [state.completedSections]);

  const getQuizScore = useCallback((quizId) => {
    return state.quizScores[quizId] ?? null;
  }, [state.quizScores]);

  const completionPercent = totalSections > 0
    ? Math.round((state.completedSections.length / totalSections) * 100)
    : 0;

  const isAllComplete = totalSections > 0 && state.completedSections.length >= totalSections;

  const resetProgress = useCallback(() => {
    const newState = {
      sessionId: state.sessionId,
      completedSections: [],
      quizScores: {},
      currentSection: null,
    };
    setState(newState);
  }, [state.sessionId]);

  return {
    ...state,
    markComplete,
    saveQuizScore,
    setCurrentSection,
    isCompleted,
    getQuizScore,
    completionPercent,
    isAllComplete,
    resetProgress,
  };
}
